import {
    type DataFrame,
    isNonBlankString,
    type MessageCreatedFrame,
    type WsEvent,
} from 'src/services/websocket/ws-protocol';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDataFrame(value: unknown): value is DataFrame {
    return isRecord(value) && typeof value.type === 'string';
}

function parseAuthError(frame: Record<string, unknown>): WsEvent {
    return typeof frame.reason === 'string' ?
        { type: 'AUTH_ERROR', reason: frame.reason } :
        { type: 'AUTH_ERROR' };
}

/**
 * Tolerant reader for the one known data frame: only the fields consumers
 * rely on for refetching can reject the frame. Unknown fields are stripped,
 * and the optional delivery-telemetry fields (`event_id`, `occurred_at`,
 * `message_uuid`) are retained when valid but never make a frame undeliverable
 * — a server-side contract addition or rollback must not sever live updates.
 */
function parseMessageCreated(frame: Record<string, unknown>): WsEvent | null {
    if (
        typeof frame.leasing_team_id !== 'number' ||
        !Number.isFinite(frame.leasing_team_id) ||
        !isNonBlankString(frame.lead_uuid) ||
        typeof frame.ts !== 'number' ||
        !Number.isFinite(frame.ts)
    ) {
        return null;
    }
    const messageCreated: MessageCreatedFrame = {
        type: 'message.created',
        leasing_team_id: frame.leasing_team_id,
        lead_uuid: frame.lead_uuid,
        ts: frame.ts,
    };
    if (isNonBlankString(frame.event_id)) {
        messageCreated.event_id = frame.event_id;
    }
    if (isNonBlankString(frame.occurred_at)) {
        messageCreated.occurred_at = frame.occurred_at;
    }
    if (typeof frame.message_uuid === 'string') {
        messageCreated.message_uuid = frame.message_uuid;
    }
    return { type: 'FRAME', frame: messageCreated };
}

/** Pure boundary parser for untrusted browser WebSocket message data. */
export function parseIncomingWsFrame(raw: unknown): WsEvent | null {
    if (typeof raw !== 'string') {
        return null;
    }

    let frame: unknown;
    try {
        frame = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!isDataFrame(frame)) {
        return null;
    }

    switch (frame.type) {
        case 'auth_ok':
            return { type: 'AUTH_OK' };
        case 'auth_error':
            return parseAuthError(frame);
        case 'message.created':
            return parseMessageCreated(frame);
        default:
            return { type: 'FRAME', frame };
    }
}
